import {type RefObject, useMemo} from 'react';

type UseTextWidthOpts = {
  /* The text to measure */
  text: string;
  /* Ref to the parent element, from which the font styles will be computed */
  parentDomRef: RefObject<HTMLElement | null>;
  /*
   * Optional CSS `font` shorthand (e.g. "12px Inconsolata"). When provided the
   * font is used directly instead of reading it off the DOM via
   * getComputedStyle — that read forces a synchronous style flush, which is
   * wasteful when the caller already knows the font. Falls back to the DOM read
   * when omitted.
   */
  font?: string;
};

const sharedCanvas = document.createElement('canvas');
const sharedContext = sharedCanvas.getContext('2d');
// Measure the provided text using an in-memory canvas. This is shared across
// all the users, to avoid creating and discarding a new canvas on every
// measurement.
function measureText(text: string, font: string): number {
  if (sharedContext == null) {
    return 0;
  }
  sharedContext.font = font;
  const width = sharedContext.measureText(text).width;
  sharedContext.clearRect(0, 0, sharedCanvas.width, sharedCanvas.height);
  return width;
}

/**
 * Hook that returns the width of the given text in pixels, based on the
 * computed font family, size, and weight of the parent element.
 * @param text
 * @param parentDomRef
 */
export function useTextWidth({
  text,
  parentDomRef,
  font,
}: UseTextWidthOpts): number {
  // oxlint-disable-next-line react/refs
  const parentElement = parentDomRef.current;

  return useMemo(() => {
    if (font != null) {
      return measureText(text, font);
    }
    if (parentElement == null) {
      return 0;
    }
    const {fontSize, fontFamily, fontWeight} = getComputedStyle(parentElement);
    return measureText(text, `${fontWeight} ${fontSize} ${fontFamily}`);
  }, [font, parentElement, text]);
}
